import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUrlName, getAPPID, getCookie } from '../Helpers/index';
import {
  NSE_GROUPS_WITH_DUE_TODAY,
  NSE_GROUPS_WITHOUT_DUE_TODAY,
  AAL_GROUPS_WITH_DUE_TODAY,
  AAL_GROUPS_WITHOUT_DUE_TODAY,
  getStepRedirectPage,
  getGroupRedirectPage,
  getStepDataTrack,
  getGroupDataTrack,
} from './constants';
import {
  StickyWrapper,
  MobileFixedWrapper,
  MobileSpacer,
  DesktopContainer,
  GroupWrapper,
  GroupLabel,
  GroupBars,
  BarSegment,
  MobileOnly,
  MobileLabels,
  MobileLabel,
  MobileGroupedBars,
} from './OrderProgressBar.styles';

/* ── Helpers ─────────────────────────────────────────────────────────── */

const flattenGroups = (groups) => groups.reduce((acc, group) => [...acc, ...group.steps], []);

const getGlobalStepIndex = (groups, pageName) => {
  const allSteps = flattenGroups(groups);
  return allSteps.findIndex((step) => step.pages.includes(pageName));
};

const getGroupStartIndex = (groups, groupIdx) => {
  let index = 0;
  for (let g = 0; g < groupIdx; g++) {
    index += groups[g].steps.length;
  }
  return index;
};

const getGroups = (isAAL, hasDueToday) => {
  if (isAAL) {
    return hasDueToday ? AAL_GROUPS_WITH_DUE_TODAY : AAL_GROUPS_WITHOUT_DUE_TODAY;
  }
  return hasDueToday ? NSE_GROUPS_WITH_DUE_TODAY : NSE_GROUPS_WITHOUT_DUE_TODAY;
};

const getGroupLabel = (group) => group.mobileLabel || group.label;

/* ── Component ───────────────────────────────────────────────────────── */

const OrderProgressBar = ({ hasDueToday: hasDueTodayOverride, flowType: flowTypeOverride }) => {
  const hasDueTodayFromStore = useSelector((state) => {
    const checkoutAmount = state?.checkout?.orderDetails?.totalDueToday;
    const cartAmount = state?.cartDetails?.cartDetailsJson?.orderDetailsView?.dueTodayAmount;
    return Number.parseFloat(checkoutAmount || cartAmount || 0) > 0;
  });
  const hasDueToday = hasDueTodayOverride ?? hasDueTodayFromStore;

  const pageName = getUrlName(window.location.pathname);
  const isAAL = flowTypeOverride ? flowTypeOverride === 'AAL' : getAPPID() === 'AAL';
  const groups = getGroups(isAAL, hasDueToday);
  const flatSteps = useMemo(() => flattenGroups(groups), [groups]);
  const currentIndex = useMemo(() => getGlobalStepIndex(groups, pageName), [pageName, groups]);

  // Mobile scroll-based sticky: render inline initially, fixed when scrolled past
  const barRef = useRef(null);
  const thresholdRef = useRef(null);
  const [isMobileFixed, setIsMobileFixed] = useState(false);
  const [barHeight, setBarHeight] = useState(50);
  const [isProgressBarEnabled, setIsProgressBarEnabled] = useState(false);
  const cdlThrottleList = getCookie('cdlThrottleList') || '';
  const isProgressBarCookieEnabled = (getCookie('enableProgressBar') || '').toUpperCase() === 'Y';

  const processScroll = useCallback(() => {
    if (window.innerWidth > 767) {
      setIsMobileFixed(false);
      return;
    }
    const rect = barRef.current?.getBoundingClientRect?.();
    if (rect && thresholdRef.current === null) {
      thresholdRef.current = rect.top + window.scrollY;
      setBarHeight(Math.ceil(rect.height));
    }
    if (thresholdRef.current !== null) {
      setIsMobileFixed(window.scrollY >= thresholdRef.current);
    }
  }, []);
  const navigate = useNavigate();

  useEffect(() => {
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processScroll();
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [processScroll]);

  // Expose global toggle so Evolv / QA / Site Ops can switch at runtime
  useEffect(() => {
    window.enableProgressBar = (enabled) => {
      const value = Boolean(enabled);
      setIsProgressBarEnabled(value);
      return value;
    };
    return () => { delete window.enableProgressBar; };
  }, []);

  if (currentIndex < 0 || !(isProgressBarEnabled || isProgressBarCookieEnabled)) return null;

  const canJumpToExpressCheckout = pageName === 'shipping' || pageName === 'appointment';

  const canNavigateToStep = (globalIndex) => {
    const step = flatSteps[globalIndex];
    const isReviewStep = step?.id === 'review';

    return globalIndex < currentIndex || (isReviewStep && canJumpToExpressCheckout);
  };

  const handleStepClick = (globalIndex) => {
    if (canNavigateToStep(globalIndex)) {
      const page = getStepRedirectPage(flatSteps[globalIndex]?.id, cdlThrottleList);
      if (page) {
        const separator = page.includes('?') ? '&' : '?';
        navigate(`/sales/home/${page}${separator}from=progressbar_${pageName}`);
      }
    }
  };

  const handleGroupClick = (groupIdx) => {
    const groupStartIndex = getGroupStartIndex(groups, groupIdx);
    const group = groups[groupIdx];
    const isReviewCheckoutGroup = group?.id === 'reviewCheckout';

    // Allow navigation to Review & Checkout from shipping/appointment to return to expresscheckout
    const canNavigate = groupStartIndex < currentIndex || 
      (isReviewCheckoutGroup && canJumpToExpressCheckout);
    
    if (canNavigate) {
      const page = getGroupRedirectPage(group?.id, cdlThrottleList);
      if (page) {
        const separator = page.includes('?') ? '&' : '?';
        navigate(`/sales/home/${page}${separator}from=progressbar_${pageName}`);
      }
    }
  };

  const handleKeyDown = (e, globalIndex) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleStepClick(globalIndex);
    }
  };

  // Mobile: current group label + next group label
  const currentGroupLabel = (() => {
    for (let g = groups.length - 1; g >= 0; g--) {
      if (currentIndex >= getGroupStartIndex(groups, g)) return getGroupLabel(groups[g]);
    }
    /* istanbul ignore next */
    return '';
  })();

  const nextGroupLabel = (() => {
    for (let g = 0; g < groups.length; g++) {
      const groupEnd = getGroupStartIndex(groups, g) + groups[g].steps.length - 1;
      if (currentIndex <= groupEnd) {
        return g + 1 < groups.length ? getGroupLabel(groups[g + 1]) : '';
      }
    }
    /* istanbul ignore next */
    return '';
  })();

  const getCurrentGroupIdx = () => groups.findIndex((group, groupIdx) => {
    const groupStart = getGroupStartIndex(groups, groupIdx);
    const groupEnd = groupStart + group.steps.length - 1;
    return currentIndex >= groupStart && currentIndex <= groupEnd;
  });

  const renderMobileBars = () => {
    const currentGroupIdx = getCurrentGroupIdx();
    const currentGroupId = currentGroupIdx > -1 ? groups[currentGroupIdx]?.id : undefined;
    const isReviewCheckoutGroup = currentGroupId === 'reviewCheckout';

    // Allow tapping Review & Checkout from shipping/appointment to return to expresscheckout
    const isCurrentGroupClickable = currentIndex > 0 || 
      (isReviewCheckoutGroup && canJumpToExpressCheckout);

    return (
    <MobileOnly>
      <MobileLabels>
        <MobileLabel
          as="button"
          isActive
          isClickable={isCurrentGroupClickable}
          onClick={() => {
            const groupIdx = getCurrentGroupIdx();
            if (groupIdx > -1) handleGroupClick(groupIdx);
          }}
          type="button"
          disabled={!isCurrentGroupClickable}
          data-track={currentGroupId ? getGroupDataTrack(currentGroupId, true) : undefined}
        >
          {currentGroupLabel}
        </MobileLabel>
        {nextGroupLabel && <MobileLabel isActive={false}>{`Next: ${nextGroupLabel}`}</MobileLabel>}
      </MobileLabels>
      <MobileGroupedBars>
        {groups.map((group, groupIdx) => {
          const groupStart = getGroupStartIndex(groups, groupIdx);
          return (
            <GroupWrapper key={`m-${group.label}`}>
              <GroupBars>
                {group.steps.map((step, stepIdx) => {
                  const globalIdx = groupStart + stepIdx;
                  const isCompleted = globalIdx <= currentIndex;
                  const isFuture = globalIdx > currentIndex;
                  const isClickable = canNavigateToStep(globalIdx);
                  const isFirstInGroup = stepIdx === 0;
                  const isLastInGroup = stepIdx === group.steps.length - 1;
                  return (
                    <BarSegment
                      key={`m-${step.id}`}
                      isCompleted={isCompleted}
                      isClickable={isClickable}
                      isFuture={isFuture}
                      isFirstInGroup={isFirstInGroup}
                      isLastInGroup={isLastInGroup}
                      onClick={() => handleStepClick(globalIdx)}
                      onKeyDown={(e) => handleKeyDown(e, globalIdx)}
                      role="button"
                      tabIndex={isClickable ? 0 : -1}
                      aria-label={group.label}
                      aria-current={globalIdx === currentIndex ? 'step' : undefined}
                      data-track={getStepDataTrack(step.id, true)}
                    />
                  );
                })}
              </GroupBars>
            </GroupWrapper>
          );
        })}
      </MobileGroupedBars>
    </MobileOnly>
    );
  };

  return (
    <>
      <StickyWrapper ref={barRef} data-testid="order-progress-bar" role="navigation" aria-label="Order progress">
        {/* ── Desktop: grouped labels + bars ── */}
        <DesktopContainer>
          {groups.map((group, groupIdx) => {
            const groupStart = getGroupStartIndex(groups, groupIdx);
            const isGroupActive = currentIndex >= groupStart;
            const isReviewCheckoutGroup = group?.id === 'reviewCheckout';

            // Allow clicking Review & Checkout from shipping/appointment to return to expresscheckout
            const isGroupClickable = groupStart < currentIndex || 
              (isReviewCheckoutGroup && canJumpToExpressCheckout);

            return (
              <GroupWrapper key={group.label}>
                <GroupLabel
                  as="button"
                  isActive={isGroupActive}
                  isClickable={isGroupClickable}
                  onClick={() => handleGroupClick(groupIdx)}
                  type="button"
                  disabled={!isGroupClickable}
                  data-track={getGroupDataTrack(group.id, false)}
                >
                  {group.label}
                </GroupLabel>
                <GroupBars>
                  {group.steps.map((step, stepIdx) => {
                    const globalIdx = groupStart + stepIdx;
                    const isCompleted = globalIdx <= currentIndex;
                    const isFuture = globalIdx > currentIndex;
                    const isClickable = canNavigateToStep(globalIdx);
                    const isFirstInGroup = stepIdx === 0;
                    const isLastInGroup = stepIdx === group.steps.length - 1;

                    return (
                      <BarSegment
                        key={step.id}
                        isCompleted={isCompleted}
                        isClickable={isClickable}
                        isFuture={isFuture}
                        isFirstInGroup={isFirstInGroup}
                        isLastInGroup={isLastInGroup}
                        onClick={() => handleStepClick(globalIdx)}
                        onKeyDown={(e) => handleKeyDown(e, globalIdx)}
                        role="button"
                        tabIndex={isClickable ? 0 : -1}
                        aria-label={group.label}
                        aria-current={globalIdx === currentIndex ? 'step' : undefined}
                        data-track={getStepDataTrack(step.id, false)}
                      />
                    );
                  })}
                </GroupBars>
              </GroupWrapper>
            );
          })}
        </DesktopContainer>

        {/* ── Mobile: inline (visible until scrolled past) ── */}
        {!isMobileFixed && renderMobileBars()}
      </StickyWrapper>

      {/* ── Mobile: fixed at top when scrolled past ── */}
      {isMobileFixed && (
        <>
          <MobileSpacer height={barHeight} aria-hidden="true" />
          <MobileFixedWrapper data-testid="order-progress-bar-fixed" role="navigation" aria-label="Order progress">
            {renderMobileBars()}
          </MobileFixedWrapper>
        </>
      )}
    </>
  );
};

OrderProgressBar.propTypes = {
  hasDueToday: PropTypes.bool,
  flowType: PropTypes.string,
};

OrderProgressBar.defaultProps = {
  hasDueToday: undefined,
  flowType: undefined,
};

export default OrderProgressBar;
